import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import XpertLogo, { XpertWordmark } from './XpertLogo';
import { navLinks, topNavLinks } from '../data/mock';

const Logo = () => (
  <Link to="/" className="flex items-center gap-2.5 group">
    <XpertLogo size={42}/>
    <XpertWordmark size="md"/>
  </Link>
);

const TopBar = () => (
  <div className="hidden md:flex justify-end gap-7 px-8 py-2 text-[13px] text-slate-600 bg-white border-b border-slate-100">
    {topNavLinks.map(l => (
      <a key={l.name} href={l.href} className="flex items-center gap-1 hover:text-[#8BC53F] transition-colors">
        {l.name}<ChevronDown className="w-3.5 h-3.5" />
      </a>
    ))}
  </div>
);

const PromoBanner = () => {
  const [show, setShow] = useState(true);
  if (!show) return null;
  return (
    <div className="relative bg-gradient-to-r from-[#0d2818] via-[#6FA82C] to-[#8BC53F] text-white">
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-center gap-4 text-sm md:text-[15px] font-medium">
        <span className="hidden sm:inline">Built for the field, powered by AI.</span>
        <span className="font-semibold">Decision Intelligence is now part of Xpertpests Solutions.</span>
        <button className="hidden md:inline-flex items-center gap-2 px-5 py-2 rounded-full border-2 border-[#F59E0B] text-white text-xs font-bold tracking-wider hover:bg-[#F59E0B]/15 transition">
          SEE IT IN ACTION
        </button>
      </div>
      <button onClick={() => setShow(false)} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white">
        <X className="w-5 h-5" />
      </button>
    </div>
  );
};

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header className="sticky top-0 z-50">
      <PromoBanner />
      <TopBar />
      <div className={`bg-white ${scrolled ? 'shadow-sm' : ''} border-b border-slate-100`}>
        <div className="max-w-7xl mx-auto px-6 h-[78px] flex items-center justify-between">
          <Logo />
          <nav className="hidden lg:flex items-center gap-7">
            {navLinks.map(l => (
              <Link key={l.name} to={l.href}
                className={`pp-nav-link flex items-center gap-1 text-[15px] font-medium ${loc.pathname === l.href ? 'text-[#8BC53F]' : 'text-slate-800'} hover:text-[#8BC53F]`}>
                {l.name}{l.hasDropdown && <ChevronDown className="w-4 h-4" />}
              </Link>
            ))}
            <Link to="/login" className="text-[15px] font-medium text-slate-800 hover:text-[#8BC53F]">Login</Link>
            <Button asChild variant="outline" className="border-[#8BC53F] text-[#6FA82C] hover:bg-[#8BC53F]/10 font-semibold px-5 h-11 rounded-md" data-testid="header-book-now">
              <Link to="/book">Book Now</Link>
            </Button>
            <Button asChild className="bg-[#8BC53F] hover:bg-[#6FA82C] text-white font-semibold px-6 h-11 rounded-md tracking-wide">
              <Link to="/pricing">GET STARTED</Link>
            </Button>
          </nav>
          <button className="lg:hidden text-slate-800" onClick={() => setOpen(!open)} aria-label="Menu">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        {open && (
          <div className="lg:hidden border-t border-slate-100 bg-white">
            <div className="px-6 py-4 flex flex-col gap-3">
              {navLinks.map(l => (
                <Link key={l.name} to={l.href} onClick={() => setOpen(false)} className="py-2 text-slate-800 font-medium">{l.name}</Link>
              ))}
              <Link to="/login" onClick={() => setOpen(false)} className="py-2 text-slate-800 font-medium">Login</Link>
              <Button asChild variant="outline" className="border-[#8BC53F] text-[#6FA82C] w-full">
                <Link to="/book" onClick={() => setOpen(false)}>Book Now</Link>
              </Button>
              <Button asChild className="bg-[#8BC53F] hover:bg-[#6FA82C] text-white w-full">
                <Link to="/pricing" onClick={() => setOpen(false)}>GET STARTED</Link>
              </Button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
